import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { useStore, formatPrice } from "@/lib/store";
import { X, Minus, Plus, ArrowRight, Loader2 } from "lucide-react";
import { cartApi } from "@/lib/api";
import { toast } from "sonner";
import { t } from "@/lib/i18n";
import { optimizeImg } from "@/lib/img";

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateLine, removeLine } = useStore();
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Prevent body scroll while drawer is open (fixes iOS Safari background scroll leak).
  useEffect(() => {
    if (!cartOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [cartOpen]);

  // Reset the checkout-loading state whenever the drawer opens/closes
  // (in case the customer aborted navigation via browser back).
  useEffect(() => { if (!cartOpen) setCheckoutLoading(false); }, [cartOpen]);

  const doCheckout = async () => {
    if (!cart || checkoutLoading) return;
    setCheckoutLoading(true);
    // If we already have the Shopify checkout URL, redirect IMMEDIATELY within
    // the same user gesture. Delaying with setTimeout breaks the user-gesture
    // chain in mobile Safari and can cause the tap to appear "dead".
    if (cart.checkout_url) {
      window.location.href = cart.checkout_url;
      return;
    }
    try {
      const url = await cartApi.checkoutUrl(cart.id, cart.market);
      window.location.href = url;
    } catch (_) {
      setCheckoutLoading(false);
      toast.error(t.toasts.checkoutFail);
    }
  };

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[70]"
            onClick={() => setCartOpen(false)}
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: [0.7, 0, 0.2, 1] }}
            className="fixed top-0 right-0 h-[100dvh] w-full sm:w-[440px] bg-white z-[80] border-l border-[color:var(--marcini-border)] flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-5 md:px-6 h-16 border-b border-[color:var(--marcini-border)]">
              <span className="text-eyebrow">{t.cart.title}{cart?.item_count ? ` · ${cart.item_count}` : ""}</span>
              <button data-testid="cart-close" aria-label={t.common.close} onClick={() => setCartOpen(false)} className="p-2 -mr-2">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 md:px-6 py-6">
              {(!cart || cart.lines.length === 0) && (
                <div className="text-center pt-20 md:pt-24" data-testid="cart-empty">
                  <p className="font-serif-editorial text-3xl mb-3">{t.cart.empty}</p>
                  <p className="text-[color:var(--marcini-fg-2)] text-sm">{t.cart.emptyBody}</p>
                </div>
              )}

              {cart?.lines.map((line) => (
                <div key={line.id} className="flex gap-4 py-5 border-b border-[color:var(--marcini-border)]" data-testid={`cart-line-${line.id}`}>
                  <img src={optimizeImg(line.image, 200)} alt={line.product_title} className="w-20 h-28 md:w-24 md:h-32 object-cover flex-shrink-0" />
                  <div className="flex-1 flex flex-col justify-between min-w-0">
                    <div>
                      <div className="flex justify-between gap-3">
                        <p className="font-serif-editorial text-lg leading-tight">{line.product_title}</p>
                        <button data-testid={`cart-remove-${line.id}`} onClick={() => removeLine(line.id)} className="text-[color:var(--marcini-fg-3)] hover:text-black flex-shrink-0 p-1 -mr-1">
                          <X size={14} />
                        </button>
                      </div>
                      <p className="text-eyebrow text-[color:var(--marcini-fg-2)] mt-1">{t.cart.size} {line.variant_title}</p>
                    </div>
                    <div className="flex justify-between items-end mt-3">
                      <div className="flex items-center border border-[color:var(--marcini-border)]">
                        <button data-testid={`cart-dec-${line.id}`} className="px-3 py-2" onClick={() => updateLine(line.id, Math.max(0, line.quantity - 1))} aria-label="−"><Minus size={12} /></button>
                        <span className="px-3 text-sm min-w-[24px] text-center">{line.quantity}</span>
                        <button data-testid={`cart-inc-${line.id}`} className="px-3 py-2" onClick={() => updateLine(line.id, line.quantity + 1)} aria-label="+"><Plus size={12} /></button>
                      </div>
                      <p className="text-sm">{formatPrice(line.unit_price)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {cart && cart.lines.length > 0 && (
              <div className="border-t border-[color:var(--marcini-border)] p-5 md:p-6 space-y-4 bg-white pb-[max(env(safe-area-inset-bottom),1.25rem)]">
                <div className="flex justify-between text-sm">
                  <span className="text-eyebrow">{t.cart.subtotal}</span>
                  <span data-testid="cart-subtotal">{formatPrice(cart.subtotal)}</span>
                </div>
                <p className="text-xs text-[color:var(--marcini-fg-3)]">{t.cart.disclaimer}</p>
                <button
                  data-testid="cart-checkout"
                  onClick={doCheckout}
                  disabled={checkoutLoading}
                  className="w-full bg-black text-white text-eyebrow py-4 flex items-center justify-center gap-3 hover:bg-black/85 active:bg-black/75 disabled:opacity-80 disabled:cursor-wait transition-colors touch-manipulation"
                >
                  {checkoutLoading ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      {t.cart.checkoutLoading}
                    </>
                  ) : (
                    <>
                      {t.cart.checkout} <ArrowRight size={14} />
                    </>
                  )}
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
